import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
          </div>
          <h2 className="text-foreground-800 font-heading text-xl font-semibold mb-2">漫画未找到</h2>
          <p className="text-foreground-400 text-sm mb-6">抱歉，您要找的漫画不存在或已下架</p>
          <Link
            to="/category"
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-all duration-300 whitespace-nowrap"
          >
            返回浏览
          </Link>
        </div>
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.categoryId === comic.categoryId)
    .slice(0, 4);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `韩漫画网 - ${comic.title}`,
            description: comic.description,
            author: {
              '@type': 'Person',
              name: comic.author,
            },
            datePublished: '2026-01-01',
            dateModified: comic.updatedAt,
            url: `http://uure9.cn/comic/${comic.id}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫画网',
              url: 'http://uure9.cn/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'http://uure9.cn/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: comic.categoryName,
                  item: `http://uure9.cn/category/${comic.categoryId}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: comic.title,
                  item: `http://uure9.cn/comic/${comic.id}`,
                },
              ],
            },
          }),
        }}
      />

      <div className="pt-20 md:pt-24 pb-16 md:pb-24 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category" className="hover:text-primary-500 transition-colors duration-200">分类浏览</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${comic.categoryId === 1 ? 'romance' : comic.categoryId === 2 ? 'action' : comic.categoryId === 3 ? 'thriller' : comic.categoryId === 4 ? 'fantasy' : comic.categoryId === 5 ? 'urban' : 'historical'}`} className="hover:text-primary-500 transition-colors duration-200">
              {comic.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-600">{comic.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="lg:w-[320px] xl:w-[380px] flex-shrink-0">
              <div className="rounded-xl overflow-hidden bg-background-100" style={{ aspectRatio: '3/4' }}>
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-foreground-400 text-xs">
                    更新于 {comic.updatedAt}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-user-line text-foreground-400"></i>
                    <span className="text-foreground-600 text-sm">{comic.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-foreground-600 text-sm font-semibold">{comic.rating}</span>
                    <span className="text-foreground-400 text-xs">评分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span className="text-foreground-600 text-sm">
                      {comic.views > 10000
                        ? `${(comic.views / 10000).toFixed(0)}万`
                        : comic.views}
                    </span>
                    <span className="text-foreground-400 text-xs">阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-price-tag-3-line text-foreground-400"></i>
                    <span className="text-foreground-600 text-sm">{comic.categoryName}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100/80 rounded-xl p-5 md:p-6 mb-6">
                  <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-3">作品简介</h2>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex-1 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm md:text-base transition-all duration-300 cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line mr-2"></i>
                    开始阅读
                  </button>
                  <button className="flex-1 px-8 py-3.5 border border-foreground-200 hover:border-foreground-400 text-foreground-700 font-medium rounded-full text-sm md:text-base transition-all duration-300 cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line mr-2"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
            读者评价
          </h2>
          <div className="max-w-2xl mx-auto">
            <div className="flex flex-col gap-4">
              {[
                {
                  user: '漫画迷小王',
                  rating: 5,
                  comment: '画风太棒了！剧情节奏把握得非常好，每一话都让人欲罢不能。韩漫画网的下拉式阅读体验很流畅。',
                  date: '2026-07-05',
                },
                {
                  user: '阅读达人',
                  rating: 4.5,
                  comment: '很喜欢这个作者的画风，人物设计很有特色。无删减版本看起来更过瘾，好评！',
                  date: '2026-07-03',
                },
                {
                  user: '韩漫爱好者',
                  rating: 5,
                  comment: '在韩漫画网追了好久了，更新速度很稳定，而且真的是完整版，没有删减，强烈推荐！',
                  date: '2026-06-28',
                },
              ].map((review, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl p-5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                        <span className="text-accent-600 text-xs font-bold">
                          {review.user[0]}
                        </span>
                      </div>
                      <span className="text-foreground-800 font-medium text-sm">{review.user}</span>
                    </div>
                    <span className="text-foreground-400 text-xs">{review.date}</span>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: Math.floor(review.rating) }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                    {review.rating % 1 !== 0 && (
                      <i className="ri-star-half-line text-accent-500 text-sm"></i>
                    )}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-3 mt-8">
              <button className="w-10 h-10 rounded-full border border-foreground-200 flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:border-foreground-400 transition-all duration-200 cursor-pointer">
                <i className="ri-arrow-left-line"></i>
              </button>
              <span className="text-foreground-600 text-sm font-medium">1 / 3</span>
              <button className="w-10 h-10 rounded-full bg-foreground-950 flex items-center justify-center text-background-50 hover:bg-foreground-800 transition-all duration-200 cursor-pointer">
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {relatedComics.length > 0 && (
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                同类推荐
              </h2>
              <p className="text-foreground-500 text-sm mt-2">更多{comic.categoryName}类漫画等您发现</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedComics.map((related) => (
                <Link
                  key={related.id}
                  to={`/comic/${related.id}`}
                  className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                    <img
                      src={related.cover}
                      alt={related.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="text-foreground-900 font-medium text-sm group-hover:text-primary-600 transition-colors duration-200 truncate">
                      {related.title}
                    </h3>
                    <p className="text-foreground-400 text-xs mt-1">
                      {related.author} · {related.rating}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="relative w-full h-[320px] md:h-[380px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20elegant%20abstract%20art%20with%20warm%20cream%20and%20gentle%20coral%20gradients%2C%20flowing%20silky%20fabric%20like%20shapes%2C%20dreamy%20atmospheric%20light%2C%20minimalist%20digital%20composition%2C%20subtle%20texture%20and%20depth%2C%20peaceful%20and%20inviting%20mood&width=1920&height=760&seq=cta-2026-x99&orientation=landscape"
            alt="韩漫画网 继续探索"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/25 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3">
              发现更多精彩韩漫
            </h2>
            <p className="text-background-50/70 text-sm md:text-base mb-6 max-w-md mx-auto">
              韩漫画网每日更新，下拉式完整版免费阅读，无尽韩漫等你探索
            </p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-8 py-3 bg-background-50 hover:bg-background-100 text-foreground-950 font-medium rounded-full text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              浏览全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}